import { useCallback, useEffect, useMemo, useState } from 'react';

interface LimitedBulkSelection {
  readonly selectedIds: ReadonlySet<string>;
  readonly toggle: (id: string) => void;
  readonly toggleAll: (checked: boolean) => void;
  readonly clear: () => void;
  readonly replace: (ids: readonly string[]) => void;
}

function sameSelection(left: ReadonlySet<string>, right: ReadonlySet<string>): boolean {
  return left.size === right.size && [...left].every((id) => right.has(id));
}

function selectedInside(selection: ReadonlySet<string>, visible: ReadonlySet<string>): Set<string> {
  return new Set([...selection].filter((id) => visible.has(id)));
}

/**
 * Seleção limitada à lista visível atual.
 *
 * O teto limita quantidade, não posição: qualquer linha visível pode ocupar uma vaga. Ao trocar
 * filtros/página, IDs ocultos deixam de contar imediatamente e também são podados do estado.
 */
export function useLimitedBulkSelection(
  visibleIds: readonly string[],
  limit: number,
): LimitedBulkSelection {
  const visible = useMemo(() => new Set(visibleIds), [visibleIds]);
  const [storedIds, setStoredIds] = useState<ReadonlySet<string>>(new Set());
  const selectedIds = useMemo(() => selectedInside(storedIds, visible), [storedIds, visible]);

  useEffect(() => {
    setStoredIds((current) => {
      const next = selectedInside(current, visible);
      return sameSelection(current, next) ? current : next;
    });
  }, [visible]);

  const toggle = useCallback(
    (id: string): void => {
      setStoredIds((current) => {
        const next = selectedInside(current, visible);
        if (!visible.has(id)) return sameSelection(current, next) ? current : next;
        if (next.has(id)) next.delete(id);
        else if (next.size < limit) next.add(id);
        return sameSelection(current, next) ? current : next;
      });
    },
    [limit, visible],
  );

  const toggleAll = useCallback(
    (checked: boolean): void => {
      setStoredIds(checked ? new Set(visibleIds.slice(0, limit)) : new Set());
    },
    [limit, visibleIds],
  );

  const clear = useCallback((): void => setStoredIds(new Set()), []);

  const replace = useCallback(
    (ids: readonly string[]): void => {
      setStoredIds(new Set(ids.filter((id) => visible.has(id)).slice(0, limit)));
    },
    [limit, visible],
  );

  return { selectedIds, toggle, toggleAll, clear, replace };
}
